"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import {
  getOfferListPriceSavings,
  type OfferListPriceSavingsT,
} from "@/app/utils/helpers/backend/offers/get-offer-list-price-savings";
import type {
  OfferFormDataT,
  OfferVehicleConditionT,
} from "@/app/types/offers";

const listPriceSavingsBadgeVariants = cva(
  "bg-primary text-primary-foreground inline-flex items-center rounded-full font-semibold tracking-normal",
  {
    variants: {
      size: {
        sm: "px-2 py-0.5 text-[11px]",
        md: "px-2.5 py-1.5 text-xs",
        lg: "px-3 py-1.5 text-sm",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  }
);

type ListPriceSavingsBadgePropsT = {
  offer: {
    pricing?: OfferFormDataT["pricing"] | null;
    offer_types?: OfferFormDataT["offer_types"];
    vehicle_condition?: OfferVehicleConditionT;
  };
  className?: string;
  footnoteMarker?: "*" | "¹" | "²";
} & VariantProps<typeof listPriceSavingsBadgeVariants>;

export const getListPriceSavingsBadgeLabel = (
  savings: OfferListPriceSavingsT,
  footnoteMarker: "*" | "¹" | "²" = "²"
): string => `Sie sparen ${savings.ersparnisProzent}%${footnoteMarker}`;

export default function ListPriceSavingsBadge({
  offer,
  className,
  size,
  footnoteMarker = "²",
}: ListPriceSavingsBadgePropsT) {
  const savings = getOfferListPriceSavings(offer);

  if (!savings) {
    return null;
  }

  return (
    <div className={cn(listPriceSavingsBadgeVariants({ size }), className)}>
      {getListPriceSavingsBadgeLabel(savings, footnoteMarker)}
    </div>
  );
}
